import React, { type FunctionComponent, useCallback, useState, useMemo, useEffect } from 'react'
import { type FormatDateOptions } from 'react-intl'
import {
  Card,
  LineChart,
  Metric,
  LoadingDots,
  PillSelector,
} from '@retailer-platform/shared-components'
import { useDomainIntl, useDomainMessages } from 'domains/reports/utils/domain/intl'
import { type PickupAnalyticsBehaviour } from 'domains/reports/components/pickup-analytics/utils/pickupAnalytics.hooks'
import { ErrorPage } from 'gin-and-tonic/containers/error-page/ErrorPage'
import { useIsPpdoTogglesEnabled } from 'domains/reports/components/pickup-analytics/utils/useIsPpdoTogglesEnabled'
import {
  type OverallPerformanceTabs,
  PickupAnalyticsChartTitles,
  type PickupAnalyticsMetric,
} from './utils/pickupAnalytics.types'
import {
  metricStyles,
  splitStyles,
  bodyStyles,
  TabContainer,
  ChartLoadingWrapper,
  ChartWrapper,
} from './utils/PickupAnalyticsContent.styles'

// if a value in the p75 wait time graph greater than this value in seconds, remove the value from the graph
// there are outliers that occur occasionally (usually on holidays) that skew the graph
// https://instacart.atlassian.net/browse/CAT-16768
const MAX_P75_WAIT_TIME_THRESHOLD = 1800

const formatUtil = {
  passThrough: (s: number) => s.toString(),
  toCommas: (s: number) => s.toLocaleString(),
  toPercentage: (s: number) => (s * 100).toFixed(2) + '%',
  toDecimal: (s: number) => s.toFixed(2),
  secondsToMinutes: (totalSeconds: number) => {
    const minutes = totalSeconds / 60

    const seconds = totalSeconds % 60

    const formattedMinutes = Math.floor(minutes)

    const formattedSeconds = ('0' + Math.floor(seconds)).slice(-2)

    return `${formattedMinutes}:${formattedSeconds}`
  },
}

const emptyMetric = {
  currentPeriodData: undefined,
  lastPeriodData: undefined,
} as const

const getSelectedTabMetricData = (
  selectedOverallPerformanceTab: OverallPerformanceTabs,
  metric: PickupAnalyticsMetric
) => {
  if (!metric) {
    return emptyMetric
  }

  if (selectedOverallPerformanceTab === PickupAnalyticsChartTitles.AllVolumeTrend) {
    return metric?.total_pickup_delivery ?? emptyMetric
  }

  if (selectedOverallPerformanceTab === PickupAnalyticsChartTitles.DeliveryVolumeTrend) {
    return metric?.delivery ?? emptyMetric
  }

  return {
    currentPeriodData: metric.currentPeriodData,
    lastPeriodData: metric.lastPeriodData,
  }
}

export const pickupAnalyticsTestIds = {
  pickups: 'pickup-analytics-metric-pickups',
  batchAcceptance: 'pickup-analytics-metric-batch-acceptance',
  p75WaitTime: 'pickup-analytics-metric-p-75-wait-time',
  secondsPerItem: 'pickup-analytics-metric-seconds-per-item',
  latePercentage: 'pickup-analytics-metric-late-percentage',
  utilizationPercentage: 'pickup-analytics-metric-utilization-percentage',
  shopperRating: 'pickup-analytics-metric-shopper-rating',
  overallPerformanceTabs: 'pickup-analytics-overall-performance-tabs',
}

interface PickupAnalyticsContentProps extends PickupAnalyticsBehaviour {}

export const PickupAnalyticsContent: FunctionComponent<PickupAnalyticsContentProps> = ({
  pickupAnalyticsData = {
    overallPerformanceSeries: {
      [PickupAnalyticsChartTitles.PickupVolumeTrend]: [],
    },
    p75WaitTimeSeries: [],
  },
  isLoading,
  dateSelectRangeValue,
  error,
  metrics,
}) => {
  const messages = useDomainMessages({
    overallPerformance: 'reportsDomain.pickupAnalytics.section.overallPerformance',
    pickups: 'reportsDomain.pickupAnalytics.metric.pickups',
    batchAcceptance: 'reportsDomain.pickupAnalytics.metric.batchAcceptance',
    customerSatisfaction: 'reportsDomain.pickupAnalytics.section.customerSatisfaction',
    p75WaitTime: 'reportsDomain.pickupAnalytics.metric.p75WaitTime',
    additionalPerformanceMetrics:
      'reportsDomain.pickupAnalytics.section.additionalPerformanceMetrics',
    secondsPerItem: 'reportsDomain.pickupAnalytics.metric.secondsPerItem',
    latePercentage: 'reportsDomain.pickupAnalytics.metric.latePercentage',
    utilizationPercentage: 'reportsDomain.pickupAnalytics.metric.utilizationPercentage',
    shopperRating: 'reportsDomain.pickupAnalytics.metric.shopperRating',
    pickupTrend: 'reportsDomain.pickupAnalytics.chart.pickup.title',
    p75WaitTimeTrend: 'reportsDomain.pickupAnalytics.chart.p75WaitTime.title',
    tooltipPickups: 'reportsDomain.pickupAnalytics.tooltip.pickups',
    tooltipSecondsPerItem: 'reportsDomain.pickupAnalytics.tooltip.secondsPerItem',
    tooltipBatchAcceptance: 'reportsDomain.pickupAnalytics.tooltip.batchAcceptance',
    tooltipP75WaitTime: 'reportsDomain.pickupAnalytics.tooltip.p75WaitTime',
    tooltipShopperRating: 'reportsDomain.pickupAnalytics.tooltip.shopperRating',
    tooltipLatePercent: 'reportsDomain.pickupAnalytics.tooltip.latePercent',
    tooltipUtilization: 'reportsDomain.pickupAnalytics.tooltip.utilization',
    all: 'reportsDomain.pickupAnalytics.overallPerformanceToggle.all',
    pickup: 'reportsDomain.pickupAnalytics.overallPerformanceToggle.pickup',
    delivery: 'reportsDomain.pickupAnalytics.overallPerformanceToggle.delivery',
  })
  const intl = useDomainIntl()

  const isPpdoTogglesEnabled = useIsPpdoTogglesEnabled()

  const formatDate = useCallback(
    (date: Date) => intl.formatDate(date, { month: 'numeric', day: 'numeric' }),
    [intl]
  )

  const formatTime = useCallback(
    (date: Date, displayTimeZone = false) => {
      const timeFormat: FormatDateOptions = { hour: '2-digit' }

      if (displayTimeZone) {
        timeFormat.timeZoneName = 'short'
      }

      return intl.formatDate(date, timeFormat)
    },
    [intl]
  )

  const { overallPerformanceSeries, p75WaitTimeSeries } = pickupAnalyticsData

  const [selectedOverallPerformanceTab, setSelectedOverallPerformanceTab] =
    useState<OverallPerformanceTabs>(
      isPpdoTogglesEnabled
        ? PickupAnalyticsChartTitles.AllVolumeTrend
        : PickupAnalyticsChartTitles.PickupVolumeTrend
    )

  useEffect(() => {
    setSelectedOverallPerformanceTab(
      isPpdoTogglesEnabled
        ? PickupAnalyticsChartTitles.AllVolumeTrend
        : PickupAnalyticsChartTitles.PickupVolumeTrend
    )
  }, [isPpdoTogglesEnabled])

  const formattedSelectedOverallPerformanceTabSeries = useMemo(
    () =>
      overallPerformanceSeries[selectedOverallPerformanceTab]?.map(({ x, y }) => ({
        y,
        x: new Date(x),
      })),
    [overallPerformanceSeries, selectedOverallPerformanceTab]
  )

  if (error) {
    return <ErrorPage action={() => window.location.reload()} />
  }

  const formatLabel = ({ x, y }: { x: Date; y: number | string }, isHourIntervals: boolean) => {
    const value = y === null ? 0 : y

    if (isHourIntervals) {
      return `${value} at ${formatTime(x, true)}`
    }
    return `${value} on ${formatDate(x)}`
  }

  const {
    secondsPerItem,
    utilizationRate,
    batchAcceptance,
    latePercentage,
    p75WaitTime,
    shopperRating,
    pickups,
  } = metrics

  const formattedP75WaitTimeSeries = p75WaitTimeSeries
    ?.map(({ x, y }) => ({
      y,
      x: new Date(x),
    }))
    .filter(({ y }) => y < MAX_P75_WAIT_TIME_THRESHOLD)

  const isHourIntervals =
    dateSelectRangeValue?.dateRange === 'today' || dateSelectRangeValue?.dateRange === 'yesterday'

  const tabs = isPpdoTogglesEnabled && (
    <TabContainer>
      <PillSelector
        testId={pickupAnalyticsTestIds.overallPerformanceTabs}
        name="overall-performance-tabs"
        items={[
          { label: messages.all, value: PickupAnalyticsChartTitles.AllVolumeTrend },
          { label: messages.pickup, value: PickupAnalyticsChartTitles.PickupVolumeTrend },
          { label: messages.delivery, value: PickupAnalyticsChartTitles.DeliveryVolumeTrend },
        ]}
        onChange={e => {
          setSelectedOverallPerformanceTab(e.target.value as unknown as OverallPerformanceTabs)
        }}
        value={selectedOverallPerformanceTab}
      />
    </TabContainer>
  )

  const pickupsCurrentPeriod = isPpdoTogglesEnabled
    ? getSelectedTabMetricData(selectedOverallPerformanceTab, pickups).currentPeriodData
    : pickups?.currentPeriodData
  const pickupsLastPeriod = isPpdoTogglesEnabled
    ? getSelectedTabMetricData(selectedOverallPerformanceTab, pickups).lastPeriodData
    : pickups?.lastPeriodData

  const batchAcceptanceCurrentPeriod = isPpdoTogglesEnabled
    ? getSelectedTabMetricData(selectedOverallPerformanceTab, batchAcceptance).currentPeriodData
    : batchAcceptance?.currentPeriodData
  const batchAcceptanceLastPeriod = isPpdoTogglesEnabled
    ? getSelectedTabMetricData(selectedOverallPerformanceTab, batchAcceptance).lastPeriodData
    : batchAcceptance?.lastPeriodData

  return (
    <div css={bodyStyles}>
      <div css={splitStyles}>
        <Card
          title={messages.overallPerformance}
          id="pickup-analytics-overallPerformance"
          headerContent={tabs}
        >
          <div css={metricStyles}>
            <Metric
              title={messages.pickups}
              valueFormatFn={formatUtil.toCommas}
              currentPeriodValue={pickupsCurrentPeriod}
              lastPeriodValue={pickupsLastPeriod}
              desiredDirection="positive"
              data-testid={pickupAnalyticsTestIds.pickups}
              tooltip={messages.tooltipPickups}
              isLoading={!pickups}
              titleAlignment="start"
            />
            <Metric
              title={messages.batchAcceptance}
              valueFormatFn={formatUtil.toPercentage}
              currentPeriodValue={batchAcceptanceCurrentPeriod}
              lastPeriodValue={batchAcceptanceLastPeriod}
              desiredDirection="positive"
              data-testid={pickupAnalyticsTestIds.batchAcceptance}
              tooltip={messages.tooltipBatchAcceptance}
              isLoading={!batchAcceptance}
              defaultValue="0"
              titleAlignment="start"
            />
          </div>
          <ChartWrapper>
            {!isLoading ? (
              <LineChart
                title={messages.pickupTrend}
                height={200}
                data={formattedSelectedOverallPerformanceTabSeries}
                scale={{ x: 'time' }}
                formatXAxis={date => formatDate(date)}
                formatTooltip={datum => formatLabel(datum, false)}
              />
            ) : (
              <ChartLoadingWrapper>
                <LoadingDots />
              </ChartLoadingWrapper>
            )}
          </ChartWrapper>
        </Card>
        <Card title={messages.customerSatisfaction} id="pickup-analytics-customerSatisfaction">
          <div css={metricStyles}>
            <Metric
              title={messages.p75WaitTime}
              valueFormatFn={formatUtil.secondsToMinutes}
              currentPeriodValue={p75WaitTime?.currentPeriodData}
              lastPeriodValue={p75WaitTime?.lastPeriodData}
              desiredDirection="negative"
              data-testid={pickupAnalyticsTestIds.p75WaitTime}
              tooltip={messages.tooltipP75WaitTime}
              isLoading={!p75WaitTime}
              titleAlignment="start"
            />
          </div>
          <ChartWrapper>
            {!isLoading ? (
              <LineChart
                title={messages.p75WaitTimeTrend}
                height={200}
                data={formattedP75WaitTimeSeries}
                scale={{ x: 'time' }}
                formatXAxis={isHourIntervals ? formatTime : formatDate}
                formatYAxis={formatUtil.secondsToMinutes}
                formatTooltip={({ x, y }) =>
                  formatLabel(
                    {
                      x,
                      y: formatUtil.secondsToMinutes(y),
                    },
                    isHourIntervals
                  )
                }
              />
            ) : (
              <ChartLoadingWrapper>
                <LoadingDots />
              </ChartLoadingWrapper>
            )}
          </ChartWrapper>
        </Card>
      </div>

      <Card title={messages.additionalPerformanceMetrics} id="additionalPerformanceMetricsTitle">
        <div css={metricStyles}>
          <Metric
            title={messages.secondsPerItem}
            valueFormatFn={formatUtil.passThrough}
            currentPeriodValue={secondsPerItem?.currentPeriodData}
            lastPeriodValue={secondsPerItem?.lastPeriodData}
            desiredDirection="negative"
            data-testid={pickupAnalyticsTestIds.secondsPerItem}
            tooltip={messages.tooltipSecondsPerItem}
            isLoading={!secondsPerItem}
            titleAlignment="start"
          />
          <Metric
            title={messages.latePercentage}
            valueFormatFn={formatUtil.toPercentage}
            currentPeriodValue={latePercentage?.currentPeriodData}
            lastPeriodValue={latePercentage?.lastPeriodData}
            desiredDirection="negative"
            data-testid={pickupAnalyticsTestIds.latePercentage}
            tooltip={messages.tooltipLatePercent}
            isLoading={!latePercentage}
            titleAlignment="start"
          />
          <Metric
            title={messages.utilizationPercentage}
            valueFormatFn={formatUtil.toPercentage}
            currentPeriodValue={utilizationRate?.currentPeriodData}
            lastPeriodValue={utilizationRate?.lastPeriodData}
            desiredDirection="positive"
            data-testid={pickupAnalyticsTestIds.utilizationPercentage}
            tooltip={messages.tooltipUtilization}
            isLoading={!utilizationRate}
            titleAlignment="start"
          />
          <Metric
            title={messages.shopperRating}
            valueFormatFn={formatUtil.toDecimal}
            currentPeriodValue={shopperRating?.currentPeriodData}
            lastPeriodValue={shopperRating?.lastPeriodData}
            desiredDirection="positive"
            data-testid={pickupAnalyticsTestIds.shopperRating}
            tooltip={messages.tooltipShopperRating}
            isLoading={!shopperRating}
            titleAlignment="start"
          />
        </div>
      </Card>
    </div>
  )
}
